import {
  inferColorVisionSubtype,
  toColorVisionCorrectnessArray,
} from "@utils/test-specific-utils/ColorVisionTestUtils";

import { VFAFlatMetrics } from "types/tests/specifics";

// Returns null rather than a severity when metrics are unavailable: the caller
// feeds this into the patient-level aggregate, and a test that produced nothing
// must abstain there instead of voting "normal".
export function getVisualFieldStatus(vfMapValues: VFAFlatMetrics | null): string | null {
  if (!vfMapValues) return null;
  const md = vfMapValues.md;
  if (md >= -2) return "normal";
  if (md >= -6) return "attention";
  return "attention";
}

export function getVisualFieldDetails(vfMapValues: VFAFlatMetrics | null): string {
  if (!vfMapValues) return "Test data not available.";
  const md = vfMapValues.md;
  if (md >= -2) {
    return "All four quadrants show normal visual field patterns. No areas of vision loss detected.";
  } else if (md >= -6) {
    return "Mild visual field defects detected. Regular monitoring recommended.";
  } else {
    return "Significant visual field loss detected. Further evaluation recommended.";
  }
}

export function getContrastScore(contrastTest: any): string {
  if (!contrastTest || !contrastTest.results) return "N/A";
  const results = contrastTest.results;

  // Check for pelliRobsonResults
  if (results.pelliRobsonResults?.lineResults) {
    const lineResults = results.pelliRobsonResults.lineResults;
    const lastLine = lineResults[lineResults.length - 1];
    if (lastLine?.letters?.length > 0) {
      const lastLetter = lastLine.letters[lastLine.letters.length - 1];
      return `${lastLetter.scale.toFixed(2)} log CS`;
    }
  }

  return "Normal";
}

export function getContrastStatus(contrastTest: any): string {
  if (!contrastTest || !contrastTest.results) return "normal";
  // Add your logic here to determine status based on contrast test results
  return "normal";
}

export function getContrastDetails(contrastTest: any): string {
  if (!contrastTest || !contrastTest.results) {
    return "Contrast sensitivity values fall within normal age ranges across all spatial frequencies.";
  }

  const results = contrastTest.results;
  if (results.pelliRobsonResults?.lineResults) {
    const lineCount = results.pelliRobsonResults.lineResults.length;
    return `Completed ${lineCount} lines of Pelli-Robson chart. Contrast sensitivity values fall within normal age ranges.`;
  }

  return "Contrast sensitivity values fall within normal age ranges across all spatial frequencies.";
}

export function getColorVisionScore(colorVisionTest: any): string {
  if (!colorVisionTest || !colorVisionTest.results) return "N/A";
  const results = toColorVisionCorrectnessArray(colorVisionTest.results);

  if (Array.isArray(results)) {
    const correctCount = results.filter((r) => r === true).length;
    const totalCount = results.length;
    return `${correctCount}/${totalCount}`;
  }

  return "N/A";
}

export function getColorVisionStatus(colorVisionTest: any): string {
  if (!colorVisionTest || !colorVisionTest.results) return "normal";
  const results = toColorVisionCorrectnessArray(colorVisionTest.results);

  if (Array.isArray(results)) {
    const correctCount = results.filter((r) => r === true).length;
    const totalCount = results.length;
    const percentage = (correctCount / totalCount) * 100;

    // If less than 80% correct, show attention
    if (percentage < 80) return "attention";
  }

  return "normal";
}

export function getVisionSummary(patientName: string, testResults: any[]): string {
  const attentionTests = testResults.filter((t) => t.status === "attention").map((t) => t.title);
  if (attentionTests.length === 0) {
    return `${patientName}'s vision test results fall within normal ranges. Continue with regular eye examinations as recommended.`;
  }
  const testList = attentionTests.join(", ");
  return `The following test(s) show results that may need follow-up: ${testList}. Please consult with your ophthalmologist for a comprehensive evaluation.`;
}

export function getColorVisionRecommendation(colorVisionTest: any): string {
  if (!colorVisionTest || !colorVisionTest.results) {
    return "Continue routine eye exams.";
  }
  const results = toColorVisionCorrectnessArray(colorVisionTest.results);
  if (!Array.isArray(results) || results.length === 0) {
    return "Continue routine eye exams.";
  }
  const incorrectCount = results.filter((r) => r !== true).length;
  if (incorrectCount === 0) {
    return "Continue routine eye exams.";
  }
  if (incorrectCount <= 3) {
    return "Consider mentioning this result at your next eye care visit.";
  }
  return "Discuss this result with an eye care professional.";
}

export function getColorVisionDetails(colorVisionTest: any): string {
  if (!colorVisionTest || !colorVisionTest.results) {
    return "All Ishihara plates correctly identified. No color vision deficiency detected.";
  }

  const results = toColorVisionCorrectnessArray(colorVisionTest.results);
  const subtype = inferColorVisionSubtype(colorVisionTest.results);
  const subtypeText =
    subtype === "protan"
      ? " Probable protan pattern."
      : subtype === "deutan"
        ? " Probable deutan pattern."
        : "";

  if (Array.isArray(results)) {
    const correctCount = results.filter((r) => r === true).length;
    const totalCount = results.length;
    const incorrectCount = totalCount - correctCount;

    if (incorrectCount === 0) {
      return `All Ishihara plates correctly identified. No color vision deficiency detected.${subtypeText}`;
    } else if (incorrectCount <= 3) {
      return `${incorrectCount} plate(s) incorrectly identified. Mild color vision deficiency may be present. Further evaluation recommended.${subtypeText}`;
    } else {
      return `${incorrectCount} plates incorrectly identified. These results could indicate irregularities in color vision.${subtypeText}`;
    }
  }

  return `All Ishihara plates correctly identified. No color vision deficiency detected.${subtypeText}`;
}
